import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from "@mui/material";
import { Save, Lock, EmojiEvents, Groups } from "@mui/icons-material";
import { fantaService } from "../services/fantaService";
import { getEventSongsData } from "../data/songsData";
import { artistImages, eurovisionArtistImages } from "../assets/artistImages";

const FantaSanremoPage = () => {
  const { user } = useSelector((state) => state.auth);
  const { eventKey } = useParams();
  const eventId = eventKey || "sanremo";
  const { songs } = getEventSongsData(eventId);
  const imagesBySongKey =
    eventId === "eurovision" ? eurovisionArtistImages : artistImages;
  const [prediction, setPrediction] = useState([null, null, null, null, null]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [results, setResults] = useState({
    results: [],
    isRevealed: false,
    isLocked: false,
  });
  const [allPredictions, setAllPredictions] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [openModal, setOpenModal] = useState(false);

  const loadAllPredictions = useCallback(async () => {
    try {
      const preds = await fantaService.getAllPredictions(eventId);
      setAllPredictions(preds);
    } catch (err) {
      console.error("Error loading all predictions:", err);
    }
  }, [eventId]);

  // Load initial data
  useEffect(() => {
    if (!user) return;

    const loadData = async () => {
      try {
        setLoading(true);
        // Load user's prediction
        const userPred = await fantaService.getUserPrediction(
          user.uid,
          eventId,
        );
        if (userPred && userPred.prediction) {
          setPrediction(userPred.prediction);
        }

        // Subscribe to results
        const unsubscribe = fantaService.subscribeToResults((data) => {
          setResults(data);

          // If revealed, load all predictions for leaderboard
          if (data && data.isRevealed) {
            loadAllPredictions();
          }
        }, eventId);

        setLoading(false);
        return () => unsubscribe();
      } catch (err) {
        console.error("Error loading data:", err);
        setError("Errore nel caricamento dei dati.");
        setLoading(false);
      }
    };

    loadData();
  }, [user, eventId, loadAllPredictions]);

  const handleArtistChange = (index, artistKey) => {
    const newPrediction = [...prediction];
    newPrediction[index] = artistKey;
    setPrediction(newPrediction);
  };

  const handleSave = async () => {
    if (prediction.some((p) => !p)) {
      setError("Seleziona tutti e 5 gli artisti per la tua Top 5!");
      return;
    }

    // Check for duplicates
    const uniqueArtists = new Set(prediction);
    if (uniqueArtists.size !== 5) {
      setError("Non puoi selezionare lo stesso artista più volte!");
      return;
    }

    try {
      setSaving(true);
      setError(null);
      await fantaService.submitPrediction(
        user.uid,
        prediction,
        user.displayName || user.email,
        eventId,
      );
      setSuccess("Pronostico salvato con successo!");
      setSaving(false);

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error("Error saving prediction:", err);
      setError("Errore nel salvataggio del pronostico.");
      setSaving(false);
    }
  };

  const calculateScore = (userPred, officialResults) => {
    if (!userPred || !officialResults || officialResults.length === 0)
      return { total: 0, details: [] };

    let score = 0;
    const details = [];

    userPred.forEach((artistKey, index) => {
      if (!artistKey) return;

      const officialIndex = officialResults.indexOf(artistKey);
      const song = songs.find((s) => s.key === artistKey);
      const artistName = song ? song.artist : "Sconosciuto";

      if (officialIndex !== -1) {
        // Artist is in top 5
        if (officialIndex === index) {
          // Exact position: 2 points
          score += 2;
          details.push({
            artist: artistName,
            points: 2,
            reason: "Posizione esatta",
          });
        } else {
          // Wrong position: 1 point
          score += 1;
          details.push({
            artist: artistName,
            points: 1,
            reason: "In classifica (posizione errata)",
          });
        }
      } else {
        details.push({
          artist: artistName,
          points: 0,
          reason: "Non in classifica",
        });
      }
    });

    return { total: score, details };
  };

  const getLeaderboard = () => {
    if (!results.results || results.results.length === 0) return [];

    return allPredictions
      .map((p) => {
        const scoreData = calculateScore(p.prediction, results.results);
        return {
          ...p,
          score: scoreData.total,
          scoreDetails: scoreData.details,
        };
      })
      .sort((a, b) => b.score - a.score);
  };

  const handleUserClick = (userEntry) => {
    setSelectedUser(userEntry);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedUser(null);
  };

  const renderPredictionForm = () => (
    <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
      <Typography
        variant="h5"
        gutterBottom
        sx={{ display: "flex", alignItems: "center", gap: 1 }}
      >
        <EmojiEvents color="primary" /> Il tuo Fanta-Pronostico
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Indovina la Top 5 finale!
        <br />
        <strong>1 punto</strong> per artista indovinato (posizione errata)
        <br />
        <strong>2 punti</strong> per artista indovinato nella posizione corretta
      </Typography>

      <Box component="form" noValidate sx={{ mt: 3 }}>
        {[0, 1, 2, 3, 4].map((index) => (
          <FormControl fullWidth key={index} sx={{ mb: 3 }}>
            <InputLabel id={`rank-${index}-label`}>
              {index + 1}° Classificato
            </InputLabel>
            <Select
              labelId={`rank-${index}-label`}
              id={`rank-${index}`}
              value={prediction[index] || ""}
              label={`${index + 1}° Classificato`}
              onChange={(e) => handleArtistChange(index, e.target.value)}
              disabled={results.isRevealed || results.isLocked}
            >
              {songs.map((song) => (
                <MenuItem
                  key={song.key}
                  value={song.key}
                  disabled={
                    prediction.includes(song.key) &&
                    prediction[index] !== song.key
                  }
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Avatar
                      src={
                        imagesBySongKey[song.key] ||
                        song.image ||
                        "/placeholder.jpg"
                      }
                      alt={song.artist}
                      sx={{ width: 24, height: 24 }}
                    />
                    {song.artist} - "{song.title}"
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ))}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Button
          fullWidth
          variant="contained"
          size="large"
          startIcon={<Save />}
          onClick={handleSave}
          disabled={saving || results.isRevealed || results.isLocked}
        >
          {saving ? "Salvataggio..." : "Salva Pronostico"}
        </Button>
      </Box>
    </Paper>
  );

  const renderResults = () => {
    const leaderboard = getLeaderboard();
    const myScore = calculateScore(prediction, results.results).total;

    return (
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 3, height: "100%" }}>
            <Typography variant="h5" gutterBottom color="primary">
              Classifica Ufficiale
            </Typography>
            <List>
              {results.results.map((artistKey, index) => {
                const song = songs.find((s) => s.key === artistKey);
                return (
                  <ListItem key={index}>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: "secondary.main" }}>
                        {index + 1}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={song ? song.artist : "Sconosciuto"}
                      secondary={song ? `"${song.title}"` : ""}
                    />
                  </ListItem>
                );
              })}
            </List>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 3, height: "100%" }}>
            <Typography
              variant="h5"
              gutterBottom
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <Groups /> Classifica Fanta-Punti
            </Typography>

            <Box sx={{ mb: 2, p: 2, bgcolor: "primary.50", borderRadius: 1 }}>
              <Typography variant="h6" align="center">
                Il tuo punteggio: {myScore} punti
              </Typography>
            </Box>

            <List>
              {leaderboard.map((entry, index) => (
                <React.Fragment key={entry.userId}>
                  <ListItem
                    button
                    onClick={() => handleUserClick(entry)}
                    alignItems="flex-start"
                    sx={{
                      bgcolor:
                        entry.userId === user.uid
                          ? "action.selected"
                          : "transparent",
                      borderRadius: 1,
                      cursor: "pointer",
                      "&:hover": { bgcolor: "action.hover" },
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: index < 3 ? "gold" : "grey.400" }}>
                        {index + 1}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle1" fontWeight="bold">
                          {entry.displayName || "Utente Anonimo"}
                          {entry.userId === user.uid && " (Tu)"}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body2" color="text.secondary">
                          Punti: {entry.score}
                        </Typography>
                      }
                    />
                  </ListItem>
                  {index < leaderboard.length - 1 && (
                    <Divider variant="inset" component="li" />
                  )}
                </React.Fragment>
              ))}
            </List>
          </Paper>
        </Grid>

        {/* User Details Modal */}
        <Dialog
          open={openModal}
          onClose={handleCloseModal}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Dettaglio Punteggio - {selectedUser?.displayName}
          </DialogTitle>
          <DialogContent dividers>
            <Typography variant="h6" gutterBottom align="center">
              Punteggio Totale: {selectedUser?.score}
            </Typography>
            <List>
              {selectedUser?.scoreDetails?.map((detail, idx) => (
                <ListItem key={idx}>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: "primary.main" }}>{idx + 1}</Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={detail.artist}
                    secondary={
                      <Box component="span">
                        <Chip
                          label={`${detail.points} pti`}
                          size="small"
                          color={detail.points > 0 ? "success" : "default"}
                          sx={{ mr: 1 }}
                        />
                        {detail.reason}
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseModal}>Chiudi</Button>
          </DialogActions>
        </Dialog>
      </Grid>
    );
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="50vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1000, mx: "auto", p: 2 }}>
      <Typography
        variant="h4"
        component="h1"
        gutterBottom
        align="center"
        sx={{ mb: 4 }}
      >
        Fanta-Pronostico
      </Typography>

      {results.isRevealed ? (
        <>
          <Alert severity="info" sx={{ mb: 4 }}>
            I risultati sono stati svelati! Ecco come è andata.
          </Alert>
          {renderPredictionForm()}
          {renderResults()}
        </>
      ) : results.isLocked ? (
        <>
          <Alert severity="info" icon={<Lock />} sx={{ mb: 4 }}>
            Pronostici chiusi! In attesa della classifica finale e dello
            svelamento dei risultati.
          </Alert>
          {renderPredictionForm()}
        </>
      ) : (
        <>
          <Alert severity="warning" icon={<Lock />} sx={{ mb: 4 }}>
            I pronostici sono segreti fino allo svelamento finale!
          </Alert>
          {renderPredictionForm()}
        </>
      )}
    </Box>
  );
};

export default FantaSanremoPage;
